import pendoInit from './pendo-init'
// import dataDogInit from './datadog-init'
import render from '../app'
import { __info, __isDebugOn, __setDebugOn } from '../init/debug'
import type { InitedMicroAppConfig, Vars, Exposes, SparkySettings, MicroAppExported, MicroAppExportedOrPromise } from '../framework/microAppTypes'

export type ElementWithParsedJson = Element & { _parsed_json_vars?: Vars }
export type MicroAppManifest = {
  id: string
  name: string
  entry?: string
  alt_entry?: string
  entry_type?: string
  generated_id?: string
  exports?: string
  env?: string
  variables?: Vars
  varsJson?: string
  dependencies?: string[]
  assets?: string[]
  prefetch_assets?: string | boolean
  is_framework?: boolean
  baseUrl?: string
}
export type MicroAppRecord = MicroAppManifest & {
  embedded?: boolean
  ep?: MicroAppExported
  promise?: Promise<MicroAppExported | undefined>
  loaded?: boolean
  error?: any
  ['+variables']?: Vars
}

export const SPARKY_FRAMEWORK = 'sparky-framework'
const isInSilentLoginIframeFn = (): boolean => {
  try {
    return window.self !== window.top && Boolean(window.frameElement) &&
      window.location.pathname === '/authentication/silent_callback'
  }
  catch (err) {
    return false
  }
}
export const isInSilentLoginIframe = isInSilentLoginIframeFn()

export const tryParseJson = (str?: string | null) => {
  if (!str) {
    return undefined
  }
  try {
    return JSON.parse(str) || undefined
  }
  catch (e) {
    console.error('failed to parse json', str)
    return undefined
  }
}

export const isObject = (o: any) =>
  Object.prototype.toString.call(o) === '[object Object]'

export const isEmptyObject = (obj: Record<string, any>) => {
  for (const _ in obj) {
    return false
  }
  return true
}

export type RenderAppConfig = {
  nav: MicroAppExportedOrPromise[]
  vars: Record<string, Vars>
  exposes: Record<string, Exposes>
  appNames: string[]
}

const renderApp = ({ nav, vars, appNames, exposes }: RenderAppConfig) => {
  const settings = Object.defineProperties({
    navigation: nav,
    variables: vars,
    exposes,
    microAppNameSet: new Set(appNames),
  }, {
    nav: { value: nav },
    vars: { value: vars },
    exports: { value: exposes },
  }) as SparkySettings
  render(settings)
}

export const initTrackers = (fxVars?: Vars) => {
  if (isInSilentLoginIframe) {
    return // skip init tracker in iframe
  }
  if (!fxVars) {
    const script = document.currentScript?.getAttribute('data-micro-app') === SPARKY_FRAMEWORK ? document.currentScript : document.querySelector(`script[data-micro-app="${SPARKY_FRAMEWORK}"]`)
    fxVars = tryParseJson(script?.getAttribute('data-app-vars'))
    if (fxVars) {
      (script as ElementWithParsedJson)._parsed_json_vars = fxVars
    }
  }
  if (fxVars) {
    // if (fxVars.datadog_rum) {
    //   dataDogInit(fxVars.datadog_rum)
    // }
    if (fxVars.pendo_api_key) {
      pendoInit(fxVars.pendo_api_key)
    }
  }
  if (!__isDebugOn()) {
    if (fxVars?.debug_mode !== undefined) {
      __setDebugOn(fxVars.debug_mode)
    }
    else if (!fxVars?.production) {
      __setDebugOn(true)
    }
  }
}

const getMicroAppEp = (id: string, varName: string | null | undefined): MicroAppExported | undefined => {
  if (!id) {
    return undefined
  }
  const app = window.__SPARKY_LIBS__?.__MICRO_APPS__?.[id]
  if (app) {
    return app.default || app
  }
  const ep = varName && (window as any)[varName]
  return ep || undefined
}

export const mergeExposes = (mergedExposes: Record<string, Exposes>, exposes: Exposes | null | undefined, appName: string, varName?: string) => {
  if (!exposes || isEmptyObject(exposes)) {
    return mergedExposes // no exposes, or the already processed
  }
  const microAppNamespaces = {} as Record<string, Exposes>
  if (varName && !exposes.default) { // convert from old to new
    const { global, ...defaults } = exposes
    if (!isEmptyObject(global)) {
      microAppNamespaces.global = global
    }
    if (!isEmptyObject(defaults)) {
      microAppNamespaces[appName] = defaults
    }
  }
  else { // new format already
    const { default: defaults, ...namespaces } = exposes
    if (!isEmptyObject(defaults)) {
      microAppNamespaces[appName] = defaults
    }
    for (const ns of Object.keys(namespaces)) {
      if (!isEmptyObject(namespaces[ns])) {
        microAppNamespaces[ns] = namespaces[ns]
      }
    }
  } // ensure no undef/null/empty obj in namespaces
  const nss = Object.keys(microAppNamespaces)
  if (!nss.length) {
    return mergedExposes // no exposes, or the already processed
  }
  for (const ns of nss) {
    const nsExisting: Exposes = mergedExposes[ns]
    const nsExposes: Exposes = microAppNamespaces[ns]
    if (nsExisting) {
      if (nsExisting === nsExposes) { // ignore same ref
        continue
      }
      for (const key of Object.keys(nsExposes)) { // shallow copy with array merge
        const expose = nsExposes[key]
        const existing = nsExisting[key]
        if (expose === undefined || expose === existing) { // undefined or same ref means ignore
          continue
        }
        if (!existing || expose === null || typeof expose === 'function') { // null to erase existing value
          nsExisting[key] = expose // overwrite
        }
        else if (Array.isArray(expose) || Array.isArray(existing)) { // merge array
          const existingArray = Array.isArray(existing) ? existing : existing ? [existing] : [] // ensure array
          nsExisting[key] = existingArray.concat(expose)
        }
        else if (isObject(expose) && (isObject(existing) || !existing)) { // merge obj
          nsExisting[key] = { ...existing, ...expose }
        }
        else { // not object
          nsExisting[key] = expose // overwrite
        }
      }
    }
    else {
      mergedExposes[ns] = nsExposes
    }
  }
  return mergedExposes
}

export const init = (microApps: MicroAppRecord[]) => {
  const { nav, vars, appNames, exposes } = microApps.reduce((acc, {
    name,
    id = name,
    exports: varName,
    variables,
    promise,
    ep = getMicroAppEp(id, varName) as MicroAppExported,
  }) => {
    if (variables) {
      if (acc.vars[name]) {
        acc.vars[id] = variables
      }
      else {
        acc.vars[name] = variables
        if (id !== name) {
          acc.vars[id] = variables
        }
      }
    }
    if (ep) {
      if (variables) {
        ep.appVars = variables
      }
      ep.id = id
      ep.appName = name
      acc.nav.push(ep)
      acc.appNames.push(name)
      const { exposes } = (ep as InitedMicroAppConfig)
      mergeExposes(acc.exposes, exposes, name, varName)
      acc.exposes.__var_names__[id] = varName // TODO: remove once format up-to-date
    }
    else if (promise) {
      const newPromise = promise.then((ep = getMicroAppEp(id, varName)) => {
        if (ep) {
          if (variables) {
            ep.appVars = variables
          }
          ep.id = id
          ep.appName = name
          const { exposes } = (ep as InitedMicroAppConfig)
          mergeExposes(acc.exposes, exposes, name, varName)
        }
        return ep
      })
      Object.defineProperties(newPromise, {
        id: { value: id },
        appName: { value: name },
      })
      acc.nav.push(newPromise)
      acc.appNames.push(name)
      acc.exposes.__var_names__[id] = varName // TODO: remove once format up-to-date
    }
    return acc
  }, {
    nav: [],
    vars: { [SPARKY_FRAMEWORK]: {} },
    exposes: Object.defineProperties({}, {
      __var_names__: { // TODO: remove once format up-to-date
        value: {} as Record<string, string | undefined>
      }
    }),
    appNames: [],
  } as RenderAppConfig)
  const admin_cluster_url = window.__admin_cluster_url || vars[SPARKY_FRAMEWORK].admin_cluster_url
  if (admin_cluster_url) {
    if (!window.__admin_cluster_url) { // for backward compat
      window.__admin_cluster_url = admin_cluster_url
    }
    // ensure window.__admin_cluster_url win
    if (vars[SPARKY_FRAMEWORK].admin_cluster_url !== admin_cluster_url) {
      vars[SPARKY_FRAMEWORK].admin_cluster_url = admin_cluster_url
    }
  }
  renderApp({ nav, vars, appNames, exposes })
}

export const staticInit = () => {
  const getMicroApp = (script: ElementWithParsedJson) => {
    const appName = script.getAttribute('data-micro-app')!
    const varName = script.getAttribute('data-global-var')
    const varsJsonStr = script.getAttribute('data-app-vars')
    const vars = script._parsed_json_vars || tryParseJson(varsJsonStr) as Vars | undefined
    if (script._parsed_json_vars) {
      delete script._parsed_json_vars
    }
    return {
      name: appName,
      exports: varName,
      variables: vars,
      ep: getMicroAppEp(appName, varName),
    } as MicroAppRecord
  }
  if (isInSilentLoginIframe) {
    const script = document.querySelector(`script[data-micro-app="${SPARKY_FRAMEWORK}"]`)
    const fx = getMicroApp(script as ElementWithParsedJson)
    __info('framework in iframe', fx)
    init([fx])
  }
  const scripts = document.querySelectorAll('script[data-micro-app]')
  const microApps = Array.from(scripts).map(getMicroApp)
  __info('micro-apps', microApps)
  init(microApps)
}

export default staticInit
